/**
 * AgentWelcomeModal — first-login greeting for new agents.
 *
 * Shows once (per browser) on first successful login/registration:
 *   - Plays the Sora 2 branded intro (falls back to <AnimatedIntro/> if the
 *     MP4 asset is missing).
 *   - Offers two CTAs: "Take the tour" (triggers driver.js) and "Skip".
 */
import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Play, X } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import AnimatedIntro from '@/components/AnimatedIntro';
import { useAgentGuidedTour } from '@/components/AgentGuidedTour';

const LS_KEY = 'waka-agent-welcome-seen';
const SORA_URL = '/videos/sora_intro.mp4';

export default function AgentWelcomeModal() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { start } = useAgentGuidedTour();
  const [open, setOpen] = useState(false);
  const [soraOk, setSoraOk] = useState(true);
  const videoRef = useRef(null);
  const tt = (en, es) => (language === 'es' ? es : en);

  useEffect(() => {
    if (!user || user.role !== 'agent') return;
    if (localStorage.getItem(LS_KEY)) return;
    // Slight delay so we don't clash with the auth-toast + service-agreement gate
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [user]);

  const close = () => {
    localStorage.setItem(LS_KEY, '1');
    setOpen(false);
  };

  const startTour = () => {
    close();
    // Give the modal 500 ms to animate out before mounting the tour
    setTimeout(() => start(), 500);
  };

  if (!user || user.role !== 'agent') return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden border-2 border-[#D4AF37]/40 bg-[#0B192C]"
        data-testid="agent-welcome-modal"
      >
        <DialogTitle className="sr-only">
          {tt('Welcome to WakaCasa', 'Bienvenido a WakaCasa')}
        </DialogTitle>
        <div className="relative">
          {/* Close X */}
          <button
            type="button"
            onClick={close}
            className="absolute z-20 top-3 right-3 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition"
            data-testid="agent-welcome-close"
            aria-label={tt('Close', 'Cerrar')}
          >
            <X className="w-4 h-4" />
          </button>

          {/* Media */}
          <div className="w-full aspect-video bg-[#0B192C]">
            {soraOk ? (
              <video
                ref={videoRef}
                src={SORA_URL}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                controls={false}
                onError={() => setSoraOk(false)}
                data-testid="agent-welcome-video"
              />
            ) : (
              <AnimatedIntro loop />
            )}
          </div>

          {/* Copy */}
          <div className="p-6 sm:p-8 text-center">
            <p
              className="uppercase tracking-[0.28em] text-[10px] font-medium mb-3"
              style={{ color: '#D4AF37' }}
            >
              {tt('Welcome, Agent', 'Bienvenido, Agente')}
            </p>
            <h2
              className="font-heading text-2xl sm:text-3xl font-semibold text-white mb-3"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              {tt(
                'Ready to sell with confidence?',
                '¿Listo para vender con confianza?'
              )}
            </h2>
            <p className="text-sm text-slate-300 mb-6 max-w-md mx-auto">
              {tt(
                "Take a 90-second interactive tour of every feature you'll use, or skip it and start listing right away.",
                'Toma un tour interactivo de 90 segundos de cada función que usarás, o sáltalo y empieza a publicar ya.'
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                onClick={startTour}
                className="gap-2 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold h-11 px-6"
                data-testid="agent-welcome-take-tour"
              >
                <Play className="w-4 h-4 fill-current" />
                {tt('Take the tour', 'Tomar el tour')}
              </Button>
              <Button
                variant="ghost"
                onClick={close}
                className="text-white/70 hover:text-white hover:bg-white/5 h-11 px-6"
                data-testid="agent-welcome-skip"
              >
                {tt('Skip for now', 'Omitir por ahora')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
